import React, { useState, useEffect } from "react";
import {
  fetchProvince,
  fetchDistricts,
  fetchSubDistricts,
  fetchVillages,
} from "../../../domain/usecases/fetchLocation";
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";
import { ProvinceModel } from "../../../domain/entities/ProvinceModel";
import { CityDistrictModel } from "../../../domain/entities/CityDistrictModel";
import { SubDistrictModel } from "../../../domain/entities/SubDistrictModel";
import { VillagesModel } from "../../../domain/entities/VillagesModel";

interface CascadingDropdownProps {
  onRegionChange : (regionId: string) => void;
};

const CascadingDropdown: React.FC<CascadingDropdownProps> = ({onRegionChange}) => {
  const [provinces, setProvinces] = useState<ProvinceModel[]>([]);
  const [districts, setDistricts] = useState<CityDistrictModel[]>([]);
  const [subDistricts, setSubDistricts] = useState<SubDistrictModel[]>([]);
  const [villages, setVillages] = useState<VillagesModel[]>([]);

  const [selectedProvince, setSelectedProvince] = useState<string>("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [selectedSubDistrict, setSelectedSubDistrict] = useState<string>("");
  const [selectedVillage, setSelectedVillage] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch provinces on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const provinceData = await fetchProvince();
        setProvinces(provinceData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching provinces:", err);
        setError("Failed to load provinces. Please try again later.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle province selection
  const handleProvinceChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const provinceId = e.target.value;
    setSelectedProvince(provinceId);

    try {
      setLoading(true);
      const districtData = await fetchDistricts(Number(provinceId));
      setDistricts(districtData);
      setSelectedDistrict("");
      setSubDistricts([]);
      setVillages([]);
    } catch (err) {
      console.error("Error fetching districts:", err);
      setError("Failed to load districts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle district selection
  const handleDistrictChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const districtId = e.target.value;
    setSelectedDistrict(districtId);

    try {
      setLoading(true);
      console.log("DISTRICTID", districtId);
      const subDistrictData = await fetchSubDistricts(districtId);
      setSubDistricts(subDistrictData);
      setSelectedSubDistrict("");
      setVillages([]);
    } catch (err) {
      console.error("Error fetching sub-districts:", err);
      setError("Failed to load sub-districts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle sub-district selection and fetch villages
  const handleSubDistrictChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subDistrictId = e.target.value; // Get the selected sub-district ID
    setSelectedSubDistrict(subDistrictId); // Update state with the selected sub-district

    try {
      setLoading(true); // Show loading spinner
      const villageData = await fetchVillages(subDistrictId); // Fetch villages using the API
      setVillages(villageData); // Update state with the fetched villages
    } catch (err) {
      console.error("Error fetching villages:", err); // Log any errors
      setError("Failed to load villages. Please try again later."); // Set error message
    } finally {
      setLoading(false); // Hide loading spinner
    }
  };

  const handleVillageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const villageId = e.target.value;
    setSelectedVillage(villageId);
    onRegionChange(villageId);
  };

  if (loading) {
    return <SpinnerLoading />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Province Dropdown */}
        <div>
          <select
            value={selectedProvince}
            onChange={handleProvinceChange}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
          >
            <option value="">Pilih Provinsi</option>
            {provinces.map((province) => (
              <option key={province.id} value={province.id}>
                {province.name}
              </option>
            ))}
          </select>
        </div>

        {/* District Dropdown */}
        {districts.length > 0 && (
          <div>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
            >
              <option value="">Pilih Kabupaten</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SubDistrict Dropdown */}
        {subDistricts.length > 0 && (
          <div>
            <select
              value={selectedSubDistrict}
              onChange={handleSubDistrictChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
            >
              <option value="">Pilih Kecamatan</option>
              {subDistricts.map((subDistrict) => (
                <option key={subDistrict.id} value={subDistrict.id}>
                  {subDistrict.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Village Dropdown */}
      {villages.length > 0 && (
        <div>
          <select
            value={selectedVillage}
            onChange={handleVillageChange}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 font-poppins"
          >
            <option value="">Pilih Desa</option>
            {villages.map((village) => (
              <option key={village.id} value={village.id}>
                {village.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default CascadingDropdown;
